import React, { useEffect, useRef, useState } from 'react';
import { isMileageDisplayEnabled } from '@utils/mileageDisplay';
import DatePicker from './DatePicker';
import SelectTypes from './SelectTypes';
import { Button, Form } from 'react-bootstrap';
import TotalSalesLists from './TotalSalesLists';
import SalesDetailModal from './SalesDetailModal';
import { ITotalSalesResponse, PAYMETHOD } from '@dataTypes/totalSalesTypes';
import { useGetTotalSalesLists } from '@hooks/totalSales/useGetTotalSalesLists';
import { format } from 'date-fns';
import storeInfo from '../../recoil/storeInfo/atom';
import { useGetMachineLists } from '@hooks/perMachine/useGetMachineLists';
import { useInView } from 'react-intersection-observer';
import TotalSalesListsSkeleton from './TotalSalesListsSkeleton';
import { useRecoilValue } from 'recoil';
import { useGetSumSalesLists } from '@hooks/totalSales/useGetSumSalesLists';
import SumSalesList from './SumSalesList';
import { useVanCancel } from '@hooks/vanCancel/useVanCancel';
import Loading from '@components/common/loading/LoadingSpinner';
import { downloadPerMachineSummarySale } from '@apis/salesDashboard';
import { useMutation } from '@tanstack/react-query';
import excelLogo from '@assets/logo/excel_logo.png';
import { isDemoSession } from '@services/demo/demoAccount';

const SelectDateWithSalesContainer = () => {
  const { ref, inView } = useInView();
  const [showSalesDetail, setShowSalesDetail] = useState(false);
  const [showDetailId, setShowDetailId] = useState<number | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSumList, setShowSumList] = useState(false);
  const [newDate, setNewDate] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const [selectedMachine, setSelectedMachine] = useState<PAYMETHOD>(PAYMETHOD.TOTAL);
  const [selectedCatId, setSelectedCatId] = useState('');

  const formattedStartDate = format(newDate[0].startDate, 'yyMMdd');
  const formattedEndDate = format(newDate[0].endDate, 'yyMMdd');
  const storeInfoState = useRecoilValue(storeInfo);
  const showMileage = isMileageDisplayEnabled(
    storeInfoState.currentStoreId,
    storeInfoState.storeLists,
  );

  const { getMachineListsData, getMachineListsLoading, getMachineListsFetching } =
    useGetMachineLists(storeInfoState.currentStoreId);

  const {
    totalSalesLists,
    isFetchingNextPage,
    isError,
    isFetching,
    isTotalSalesLoading,
    fetchNextPage,
    setSearchTrigger,
    hasNextPage,
  } = useGetTotalSalesLists({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    catId: selectedCatId,
    method: selectedMachine,
    storeId: storeInfoState.currentStoreId,
  });

  const { sumSalesListData, sumLoading, sumFetching, setSearchSumTrigger } = useGetSumSalesLists({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    catId: selectedCatId,
    method: selectedMachine,
    storeId: storeInfoState.currentStoreId,
    showSumList: showSumList,
  });

  const {
    mutateAsync: downloadPerMachineSummarySaleAsync,
    isLoading: downloadPerMachineSummarySaleLoading,
  } = useMutation(() =>
    downloadPerMachineSummarySale(
      storeInfoState.currentStoreId,
      formattedStartDate,
      formattedEndDate,
      selectedCatId,
      selectedMachine,
    ),
  );

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  useEffect(() => {
    setSelectedCatId('');
  }, [storeInfoState]);

  const demoSearched = useRef(false);
  useEffect(() => {
    if (!isDemoSession() || !storeInfoState.currentStoreId || demoSearched.current) return;
    demoSearched.current = true;
    setSearchTrigger(true);
  }, [storeInfoState.currentStoreId]);

  useEffect(() => {
    if (!showMileage && selectedMachine === PAYMETHOD.CASH) {
      setSelectedMachine(PAYMETHOD.TOTAL);
    }
  }, [showMileage, selectedMachine]);

  const handleSearch = () => {
    setSearchTrigger(true);
    setSearchSumTrigger(true);
  };

  const handleSalesDetailShow = (id: number) => {
    setShowDetailId(id);
    setShowSalesDetail(true);
  };
  const handleSalesDetailClose = () => setShowSalesDetail(false);

  const detailModalData = totalSalesLists?.find((list) => list.id === showDetailId);

  const { vanCancelAsync, vanCancelStatusData, vanCancelStatusLoading } = useVanCancel(
    detailModalData?.id,
    detailModalData?.vanKey,
  );

  if (isError) {
    return <>error</>;
  }

  return (
    <div className="select_date_with_sales_container" data-tour="total-sales">
      <DatePicker
        showDatePicker={showDatePicker}
        setShowDatePicker={setShowDatePicker}
        newDate={newDate}
        setNewDate={setNewDate}
      />
      <SelectTypes
        setSelectedMachine={setSelectedMachine}
        machineLists={getMachineListsData}
        setSelectedCatId={setSelectedCatId}
        showMileage={showMileage}
      />
      <div className="btn_wrapper" onClick={handleSearch}>
        <Button className="search_btn">검색</Button>
      </div>

      <div className="download_btn_wrapper">
        <Form className="switch_wrapper">
          <Form.Check // prettier-ignore
            type="switch"
            id="custom_switch"
            label="통합매출"
            onChange={() => setShowSumList((prevState) => !prevState)}
          />
        </Form>

        {totalSalesLists && totalSalesLists?.length > 0 && (
          <div className="sales_excel_wrapper" onClick={() => downloadPerMachineSummarySaleAsync()}>
            <img className="logo" src={excelLogo} alt="엑셀 로고" />
            <span className="text">다운로드</span>
          </div>
        )}
      </div>

      {showSumList && <SumSalesList item={sumSalesListData} showMileage={showMileage} />}

      {isTotalSalesLoading ? (
        <div className="loading_wrapper">
          <Loading />
        </div>
      ) : (
        <ul className="total_sales_lists_wrapper">
          {!isFetchingNextPage && totalSalesLists?.length === 0 ? (
            <div className="no_data">데이터가 없습니다.</div>
          ) : (
            <>
              {totalSalesLists?.map((list, index) => (
                <TotalSalesLists
                  key={index}
                  showDetailPayment={handleSalesDetailShow}
                  item={list}
                  cancelBtn={null}
                  showMileage={showMileage}
                />
              ))}
              {isFetchingNextPage || isFetching ? (
                <TotalSalesListsSkeleton />
              ) : (
                <div ref={ref} className="targetRef"></div>
              )}
            </>
          )}

          {totalSalesLists?.length > 0 && !hasNextPage && (
            <div className="last_page">마지막 페이지 입니다.</div>
          )}
        </ul>
      )}
      {detailModalData && !vanCancelStatusLoading && (
        <SalesDetailModal
          show={showSalesDetail}
          handleClose={handleSalesDetailClose}
          detailModalData={detailModalData}
          showMileage={showMileage}
          vanCancelAsync={vanCancelAsync}
          vanCancelStatusData={vanCancelStatusData}
        />
      )}
    </div>
  );
};

export default SelectDateWithSalesContainer;
